import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket, Tag } from 'lucide-react';
import { displayMomentDate, safeHttpUrl } from '../git-format';
import { type GitDevelopmentSlotProps } from '../types';

const CHIP =
  'inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium';

/**
 * Chip **"có trong `v1.4.0`"** và **"đã lên production dd/MM/yyyy"** trong khối
 * "Phát triển" của task / issue — APM-GIT-i, docs/plan-git-integration.md §8.2.
 * Cắm ở `GIT_DEVELOPMENT_SLOTS`.
 *
 * Dữ liệu đã nằm trong `GitTargetLinks` (`releases`, `deployedAt`) mà khối vừa đọc
 * — không lời gọi nào thêm.
 *
 * "Đã lên production" là **ước lượng** (§7.1, `estimateDeployment`): chip ghi rõ chữ
 * "ước lượng" và `title` nói vì sao có thể sai — một mốc trông chắc chắn mà sai là
 * thứ người ta đem đi báo khách hàng.
 *
 * ⚠️ Không nhận asOf (luật #7, ngoại lệ 1): thuần trình bày — `links` đã được khối
 * "Phát triển" đọc **tại `asOf`** (bản có `releasedAt ≤ asOf`, deployment xong trước
 * mốc). Prop `asOf` của chỗ cắm bị bỏ qua có chủ ý.
 */
export function GitReleaseChipsSlot({ links }: GitDevelopmentSlotProps): ReactNode {
  const { t } = useTranslation();
  const releases = links.releases ?? [];
  const deployedAt = links.deployedAt ?? null;
  if (releases.length === 0 && deployedAt === null) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="git-release-chips">
      {releases.map((release) => {
        const href = safeHttpUrl(release.webUrl);
        const title = t('gitTab.releaseChips.inReleaseTitle', {
          tag: release.tag,
          date: displayMomentDate(release.releasedAt),
          repository: release.repositoryPath,
        });
        const body = (
          <>
            <Tag aria-hidden className="size-3 shrink-0" />
            <span className="truncate">
              {t('gitTab.releaseChips.inRelease', { tag: release.tag })}
            </span>
          </>
        );
        return href ? (
          <a
            key={release.id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
            data-testid="git-release-chip"
            className={`${CHIP} bg-primary/10 text-primary hover:underline`}
          >
            {body}
          </a>
        ) : (
          <span
            key={release.id}
            title={title}
            data-testid="git-release-chip"
            className={`${CHIP} bg-primary/10 text-primary`}
          >
            {body}
          </span>
        );
      })}
      {deployedAt !== null ? (
        <span
          data-testid="git-deployed-chip"
          title={t('gitTab.releaseChips.deployedTitle')}
          className={`${CHIP} bg-rag-green/15 text-rag-green`}
        >
          <Rocket aria-hidden className="size-3 shrink-0" />
          {t('gitTab.releaseChips.deployed', { date: displayMomentDate(deployedAt) })}
          <span className="font-normal opacity-80">({t('gitTab.releaseChips.estimate')})</span>
        </span>
      ) : null}
    </div>
  );
}
